import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Select, Tooltip, Typography } from 'antd'
import { Floor } from 'models/Building'
import ViewerFloorMap from 'pages/building/buildingDetailViewMode/components/ViewerFloorMap'
import { useRecoilState } from 'recoil'
import { floorDetailState, listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'
import useSWR from 'swr'
import { FloorDetail, FloorMapElement } from 'types/building'
import Utils from 'utils'

import Spinner from 'components/atoms/Spinner'
import DropdownIconCustom from 'components/icons/DropdownIconCustom'

import CommunityViewerAreaDropdown from './CommunityViewerAreaDropdown'

type Props = {
  floors: Floor[]
  refetch: () => void
}

export default function CommunityFloorDetail({ floors, refetch }: Props) {
  const { t } = useTranslation()
  const { id } = useParams()
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [, setFloorDetail] = useRecoilState(floorDetailState)
  const [, setPoints] = useRecoilState(listPointState)

  const listFloor = useMemo(
    () =>
      floors?.map((floor) => {
        const isTitle = floor.name.length > 21 ? floor.name : undefined
        return {
          value: floor.id,
          label: (
            <Tooltip placement="right" title={isTitle}>
              <span className="text-[#021120] text-sm">
                {Utils.truncateString(floor.name, 21)}
              </span>
            </Tooltip>
          )
        }
      }) || [],
    [floors]
  )

  const { isValidating: isValidatingFloor, mutate: refetchFloor } =
    useSWR<FloorDetail>(
      {
        url: `v1/tour/floors/${currentViewer?.selectedFloor}`
      },
      {
        onSuccess: (data) => {
          setFloorDetail(data)
        },
        onError: () => {
          setCurrentViewer((prev) => ({ ...prev, selectedFloor: undefined }))
          setFloorDetail(null)
          refetch()
        }
      }
    )

  const { data: mapItems, isValidating: isValidatingPoints } =
    useSWR<FloorMapElement>({
      url: 'v1/tour/points',
      params: {
        buildingId: id,
        floorId: currentViewer?.selectedFloor
      }
    })

  const handleSelectFloor = (value: number) => {
    setCurrentViewer((prev) => ({
      ...prev,
      selectedArea: undefined,
      selectedPoint: undefined,
      selectedFloor: value,
      isClickToNextFloor: true,
      isClickToDirection: false,
      isSelectPointInLeftMenu: true
    }))
  }

  useEffect(() => {
    if (mapItems && !isValidatingPoints) {
      setPoints(mapItems.points)
      // prevent select default selected point when navigate
      if (currentViewer.selectedPoint) {
        setCurrentViewer((prev) => ({
          ...prev,
          selectedArea: `${mapItems.points.find(
            (item) => item.id === currentViewer.selectedPoint
          )?.areaId}`
        }))
        return
      }

      const image360Points = mapItems.points.filter((item) => item.image360)

      const defaultPoint =
        mapItems.defaultPoint ||
        (image360Points.length
          ? image360Points.reduce((acc, curr) =>
              curr.order < acc.order ? curr : acc
            )
          : null)

      defaultPoint &&
        setCurrentViewer((prev) => ({
          ...prev,
          selectedArea: `${defaultPoint.areaId}`,
          selectedPoint: defaultPoint.id
        }))
    }
  }, [
    currentViewer.selectedPoint,
    isValidatingPoints,
    mapItems,
    setCurrentViewer,
    setPoints
  ])

  // set default floor in fist loading
  useEffect(() => {
    if (isFirstLoad) {
      setCurrentViewer((prev) => ({
        ...prev,
        selectedFloor: listFloor[0]?.value
      }))
    }
    listFloor.length && setIsFirstLoad(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstLoad, listFloor])

  return (
    <>
      <div className="flex flex-col gap-2 p-[10px]">
        <Typography className="font-bold text-white">
          {t('common.sidebar.floor')}
        </Typography>
        <Select
          style={{
            width: 'full',
            overflow: 'hidden'
          }}
          options={listFloor}
          value={!isValidatingFloor ? currentViewer?.selectedFloor : undefined}
          onSelect={handleSelectFloor}
          defaultValue={listFloor[0]?.value}
          disabled={!listFloor.length}
          className={!listFloor.length ? 'select-floot-disabled' : ''}
          suffixIcon={<DropdownIconCustom colorIcon="#021120" />}
        />
      </div>

      {!isValidatingFloor && !isValidatingPoints ? (
        <>
          <ViewerFloorMap refetchFloor={refetchFloor} />

          <div className="h-full overflow-auto">
            <CommunityViewerAreaDropdown />
          </div>
        </>
      ) : (
        <Spinner />
      )}
    </>
  )
}
